import {CUALQUIER_DOCUMENTO, type TipoDocumento} from '@/lib/documento'

// Espejo de backend app/core/validation.py con las mismas claves errors.*; la API sigue siendo la puerta.
// Topes y patrones se leen de `lib/campos-texto.ts`, nunca se escriben acá.

// DOC y CIIU no tienen validador propio porque hoy ningún campo los pide; `validarClase` ya los cubre.
import {patron, tope} from '@/lib/campos-texto'

const NIT_DV_WEIGHTS = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71]

function nitCheckDigit(base: string): number {
  const sum = [...base].reverse().reduce((acc, digit, i) => acc + Number(digit) * NIT_DV_WEIGHTS[i], 0)
  const r = sum % 11
  return r < 2 ? r : 11 - r
}

export function validateNit(raw: string): string | null {
  const compact = raw.replaceAll('.', '').replaceAll(' ', '')
  const at = compact.lastIndexOf('-')
  if (at === -1) return 'errors.nitMissingDv'
  const base = compact.slice(0, at)
  const dv = compact.slice(at + 1)
  // La clase NIT acota la forma escrita, con puntos; compactada queda la base de 6 a 10 dígitos de la DIAN.
  if (!/^\d{6,10}$/.test(base) || !/^\d$/.test(dv)) return 'errors.nitBadFormat'
  return Number(dv) === nitCheckDigit(base) ? null : 'errors.nitWrongDv'
}

/** Espeja `core/documento.py::validar_documento`. Conserva nombre y firma porque R5 de
 * el check `doctrina_de_ui` lo busca por nombre en todo archivo con un campo de cédula. */
export function validateCedula(raw: string, tipo: TipoDocumento | typeof CUALQUIER_DOCUMENTO = 'CC'): string | null {
  const alfanumerico = patron('DOC').test(raw.trim())
  if (tipo === CUALQUIER_DOCUMENTO) return alfanumerico ? null : 'errors.documentoBadFormat'
  if (tipo === 'PA') return alfanumerico ? null : 'errors.pasaporteBadFormat'
  return patron('CEDULA').test(raw.trim()) ? null : 'errors.cedulaBadFormat'
}

/** Espeja `validar_nombre` de `app/models/formatos.py`: solo largo y dígitos, sin el patrón de NOM, para
 * no ser más estricto que la API. */
export function validateNombre(raw: string): string | null {
  const nombre = raw.trim()
  if (nombre.length > tope('NOM')) return 'errors.nombreTooLong'
  return /\d/.test(nombre) ? 'errors.nombreConDigitos' : null
}

/** Espeja `validar_anio` de `app/models/formatos.py`: entre 1900 y el año próximo. */
export function validateAnio(raw: string): string | null {
  const anio = raw.trim()
  if (!patron('ANIO').test(anio)) return 'errors.anioNormaInvalido'
  const n = Number(anio)
  // Pista del formulario: el backend valida el rango con su propio reloj.
  // nosemgrep: browser-clock-business-date
  return n >= 1900 && n <= new Date().getFullYear() + 1 ? null : 'errors.anioNormaInvalido'
}

export function validateRequired(raw: string): string | null {
  return raw.trim() ? null : 'errors.required'
}

/** El backend acota además por arriba (1.000.000). */
export function validateWorkerCount(raw: string): string | null {
  return /^[1-9]\d*$/.test(raw.trim()) ? null : 'errors.workerCountInvalid'
}

/** Espeja `amount_cop >= 0`: el 0 es un override legítimo. Se perdonan separadores de miles, no decimales. */
export function validateAmountCop(raw: string): string | null {
  const compact = raw.trim().replaceAll('.', '').replaceAll(' ', '')
  return /^\d+$/.test(compact) ? null : 'errors.amountCopInvalid'
}

/** Los mismos separadores que perdonó `validateAmountCop`, para enviar el valor que se validó. */
export function parseAmountCop(raw: string): number {
  return Number(raw.trim().replaceAll('.', '').replaceAll(' ', ''))
}

export function validateAccepted(checked: boolean): string | null {
  return checked ? null : 'errors.mustAccept'
}

export function validateEmail(raw: string): string | null {
  const email = raw.trim()
  if (email.length > tope('MAIL')) return 'errors.emailTooLong'
  return patron('MAIL').test(email) ? null : 'errors.emailBadFormat'
}

/** 7 dígitos (fijo local) o 10 (celular o fijo con la marcación nacional de 2022). Solo largo, sin exigir
 * 3 o 6 al inicio: rechazar un número real escrito de forma legítima es peor. */
export function validatePhone(raw: string): string | null {
  const digits = raw.replace(/[\s().-]/g, '')
  return patron('TEL').test(digits) ? null : 'errors.phoneBadFormat'
}

/** Solo para contraseñas nuevas, nunca al iniciar sesión. Espeja validate_password de app/core/validation.py. */
export function validatePassword(raw: string): string | null {
  if (raw.length < 8) return 'errors.passwordTooShort'
  return /[^A-Za-z0-9]/.test(raw) ? null : 'errors.passwordNoSpecial'
}

/** Convierte metros («1,70») a centímetros; un «1» o «2» sueltos no bastan para adivinar la unidad.
 * El resultado igual pasa por `parseIntInRange`. */
export function normalizeTallaCm(raw: string): string {
  const compact = raw.trim().replace(',', '.')
  if (!/^[12]\.\d{1,2}$/.test(compact)) return raw
  return String(Math.round(Number.parseFloat(compact) * 100))
}

/** Vacío es válido; devuelve la clave i18n y `FieldError` interpola los límites. */
export function parseIntInRange(raw: string, min: number, max: number): {value: number | null; error: string | null} {
  const trimmed = raw.trim()
  if (trimmed === '') return {value: null, error: null}
  const parsed = Number.parseInt(trimmed, 10)
  if (Number.isNaN(parsed) || parsed < min || parsed > max) return {value: null, error: 'errors.numberOutOfRange'}
  return {value: parsed, error: null}
}

/** Solo `http://` o `https://`: un auditor abre este enlace, y `javascript:` o `data:` serían un vector.
 * Espeja `fuente_url_valida` de `app/core/normas_propias.py`. */
export function validateUrl(raw: string): string | null {
  const url = raw.trim()
  if (url === '') return null
  return patron('URL').test(url) ? null : 'errors.urlBadFormat'
}
